import { useState, useEffect } from 'react';
import { useAuth } from '../components/hooks/AuthContext';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';

// Import AdminLogin from hooks
import { AdminLogin } from '../components/hooks/hooks';

const sessionExpired = () =>
  new URLSearchParams(window.location.search).get('expired') === '1';

export default function LoginPage() {
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', severity: '' });
  const [isLoading, setIsLoading] = useState(false);
  const { login, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  
  // Redirect if already logged in
  useEffect(() => {
    if (isLoggedIn) {
      navigate('/');
    }
  }, [isLoggedIn, navigate]);
  
  // Form validation schema using Yup
  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: Yup.string()
      .required('Password is required')
  });

  const handleLogin = async (values) => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await AdminLogin({ email: values.email, password: values.password });
      
      if (response && response.token && response.user) {
        const user = response.user;
        const isSuperUser = user.is_superuser || user.is_admin || false;
        
        if (!isSuperUser) {
          setToast({
            open: true,
            message: "Access denied. This portal is restricted to super administrators only.",
            severity: "error"
          });
          localStorage.removeItem("token");
          localStorage.removeItem("authToken");
          return;
        }
        
        localStorage.setItem("token", response.token);
        localStorage.setItem("authToken", response.token);
        
        setToast({
          open: true,
          message: `Welcome, Super Administrator ${user.name || user.email}!`,
          severity: "success"
        });
        
        login(user);
        
        setTimeout(() => {
          navigate('/');
        }, 500);
        
      } else {
        setToast({
          open: true,
          message: "Invalid email or password",
          severity: "error"
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      
      let errorMessage = "An error occurred during login";
      
      if (error.response?.status === 401) {
        errorMessage = "Invalid email or password";
      } else if (error.response?.status === 403) {
        errorMessage = "Access denied. You do not have sufficient privileges.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setToast({
        open: true,
        message: errorMessage,
        severity: "error"
      });
      
      localStorage.removeItem("token");
      localStorage.removeItem("authToken");
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      agreedToTerms: false
    },
    validationSchema,
    onSubmit: handleLogin,
  });

  return (
    <div className="flex h-screen w-full">
      {/* Left side */}
      <div className="hidden md:flex md:w-5/12 bg-[#001f3f] flex-col items-center justify-between text-white p-8 relative">
        <div /> {/* spacer */}

        <div className="max-w-md text-center">
          {/* Security Notice Badge */}
          <div className="mb-6 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            Administration Portal
          </div>
          
          <h2 className="text-2xl font-bold mb-4">JamboRicG Admin Portal</h2>
          
          <p className="text-lg mb-4">
            This is Admin Panel for managing Jambo content, this is for Jambo staffs! No one is allowed to access this without Authorization.
          </p>
          
          <div className="bg-red-900 bg-opacity-50 border border-red-300 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-300 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="text-left">
                <h4 className="text-sm font-medium text-red-100">Restricted Access</h4>
                <p className="text-xs text-red-200 mt-1">Only administrators can access this portal</p>
              </div>
            </div>
          </div>
          
          <p className="text-sm opacity-75">
            Welcome to JamboRicG Admin Portal!
          </p>
          
          <div className="flex justify-center mt-8 space-x-2">
            <div className="h-2 w-2 rounded-full bg-white opacity-50"></div>
            <div className="h-2 w-2 rounded-full bg-white"></div>
            <div className="h-2 w-2 rounded-full bg-white opacity-50"></div>
          </div>
        </div>

        {/* Privacy policy link - left panel bottom */}
        <div className="text-center">
          <p className="text-blue-300 text-xs mb-1">By accessing this portal, you agree to our</p>
          <button
            onClick={() => navigate('/privacy-policy')}
            className="text-yellow-400 text-xs font-semibold hover:text-yellow-300 transition-colors underline underline-offset-2"
          >
            Privacy Policy
          </button>
        </div>
      </div>
      
      {/* Right side - Login form */}
      <div className="w-full md:w-7/12 flex flex-col items-center justify-between p-4">
        <div className="w-full max-w-md p-6 flex-1 flex flex-col justify-center">
          <div className="mb-8">
            <div className="flex justify-center items-center mb-8">
              <img 
                src="/images/ricg_logo.png" 
                alt="RicG Logo" 
                className="h-28 mr-2"
              />
            </div>
            
            <h2 className="text-3xl font-bold text-center text-[#001f3f] mb-4">Admin Login</h2>
            
            <div className="text-center mb-4">
              <p className="text-gray-500 mb-2">
                Secure administrator access to manage Jambo content and system operations.
              </p>
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                Super Admin Privileges Required
              </div>
            </div>
            
            <div className="my-6 border-t border-gray-200"></div>
          </div>
          
          {sessionExpired() && (
            <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Your session expired after 4 hours of inactivity. Please sign in again.
            </div>
          )}

          <form onSubmit={formik.handleSubmit}>
            <div className="mb-6">
              <label className="block text-gray-600 mb-2">Admin Email</label>
              <input
                type="email"
                id="email"
                name="email"
                className={`w-full p-3 border ${formik.touched.email && formik.errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-[#001f3f]`}
                placeholder="Enter your admin email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.email && formik.errors.email && (
                <div className="text-red-500 text-sm mt-1">{formik.errors.email}</div>
              )}
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-600 mb-2">Admin Password</label>
              <input
                type="password"
                id="password"
                name="password"
                className={`w-full p-3 border ${formik.touched.password && formik.errors.password ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-[#001f3f]`}
                placeholder="Enter your admin password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.password && formik.errors.password && (
                <div className="text-red-500 text-sm mt-1">{formik.errors.password}</div>
              )}
            </div>
            
            {toast.open && (
              <div className={`mb-4 p-3 rounded-md text-sm border ${
                toast.severity === 'success' 
                  ? 'bg-green-50 text-green-700 border-green-200' 
                  : 'bg-red-50 text-red-700 border-red-200'
              }`}>
                <div className="flex items-center">
                  {toast.severity === 'success' ? (
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  )}
                  {toast.message}
                </div>
              </div>
            )}
            
            <button
              type="submit"
              className="w-full bg-[#001f3f] hover:bg-[#002f5f] text-white py-3 rounded-md transition duration-200 flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying Access...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Secure Login
                </>
              )}
            </button>
          </form>
          
          {/* Security footer */}
          <div className="mt-6 text-center">
            <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-md">
              <div className="flex items-center justify-center mb-1">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Secure Connection
              </div>
              This portal requires super administrator privileges.
              <br />
              Unauthorized access attempts are logged and monitored.
            </div>
          </div>
        </div>

        {/* Privacy Policy footer - right panel bottom */}
        <div className="w-full max-w-md px-6 pb-4 text-center">
          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs text-gray-400">
              By signing in, you acknowledge and agree to our{' '}
              <button
                onClick={() => navigate('/privacy-policy')}
                className="text-[#001f3f] font-semibold hover:text-yellow-600 transition-colors underline underline-offset-2"
              >
                Privacy Policy
              </button>
            </p>
            <p className="text-xs text-gray-300 mt-1">
              © {new Date().getFullYear()} JamboRicG. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}